/**
 * DocumentsTab.jsx
 *
 * Reusable documents UI for resident/staff documents.
 */

import React from "react";
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    Stack,
    CircularProgress,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString() : "Unknown";

const isExpired = (doc) => {
    if (!doc?.expiresAt) return false;
    const expiry = new Date(doc.expiresAt);
    if (Number.isNaN(expiry.getTime())) return false;
    return expiry < new Date();
};

export default function DocumentsTab({
    title,
    documentTypes,
    documents,
    docType,
    onChangeDocType,
    customTypeName,
    onChangeCustomTypeName,
    expiresAt,
    onChangeExpiresAt,
    uploading,
    onSelectFile,
    onFileSelected,
    onDelete,
    fileInputRef,
    groupByType = false,
    emptyText = "No documents uploaded yet.",
    showTypeInSecondary = false,
    typeLabelMap = {},
    isDisabled = false
}) {
    const documentsByType = groupByType
        ? documents.reduce((acc, doc) => {
            const key = doc.type || "OTHER";
            if (!acc[key]) acc[key] = [];
            acc[key].push(doc);
            return acc;
        }, {})
        : null;

    const orderedTypes = groupByType
        ? documentTypes.map((t) => t.value).filter((type) => documentsByType[type]?.length)
        : [];
    const extraTypes = groupByType
        ? Object.keys(documentsByType).filter((type) => !orderedTypes.includes(type))
        : [];
    const groupedTypes = groupByType ? [...orderedTypes, ...extraTypes] : [];

    const buildSecondary = (doc) => {
        const parts = [];
        if (showTypeInSecondary) {
            parts.push(typeLabelMap[doc.type] || doc.type || "Other");
        }
        parts.push(`Uploaded ${formatDate(doc.createdAt)}`);
        if (doc.expiresAt) {
            parts.push(`Expires ${formatDate(doc.expiresAt)}`);
        }
        return parts.join(" • ");
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {title}
            </Typography>

            <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={onFileSelected}
            />

            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mb: 3 }}
                alignItems={{ xs: "stretch", sm: "center" }}
            >
                <FormControl
                    size="small"
                    fullWidth
                    sx={{ minWidth: { sm: 200 } }}
                >
                    <InputLabel>Document Type</InputLabel>
                    <Select
                        label="Document Type"
                        value={docType}
                        disabled={isDisabled}
                        onChange={(e) => onChangeDocType(e.target.value)}
                    >
                        {documentTypes.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {docType === "OTHER" && (
                    <TextField
                        fullWidth
                        size="small"
                        label="Custom Type"
                        disabled={isDisabled}
                        value={customTypeName}
                        onChange={(e) => onChangeCustomTypeName(e.target.value)}
                    />
                )}

                <TextField
                    fullWidth
                    type="date"
                    size="small"
                    label="Expires On"
                    InputLabelProps={{ shrink: true }}
                    disabled={isDisabled}
                    value={expiresAt}
                    onChange={(e) => onChangeExpiresAt(e.target.value)}
                />

                <Button
                    variant="outlined"
                    onClick={onSelectFile}
                    disabled={uploading || isDisabled}
                    sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
                >
                    Select File
                </Button>

                {uploading && <CircularProgress size={24} />}
            </Stack>

            {documents.length === 0 && (
                <Typography color="text.secondary">
                    {emptyText}
                </Typography>
            )}

            {groupByType ? (
                <Stack spacing={2}>
                    {groupedTypes.map((type) => (
                        <Box key={type}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                            >
                                {typeLabelMap[type] || type}
                            </Typography>
                            <List>
                                {documentsByType[type].map((doc) => (
                                    <ListItem
                                        key={doc.id}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                color="error"
                                                disabled={isDisabled}
                                                onClick={() => onDelete(doc.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <a
                                                        href={doc.fileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {doc.fileName}
                                                    </a>
                                                    {isExpired(doc) && (
                                                        <Chip
                                                            label="Expired"
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>
                                            }
                                            secondary={buildSecondary(doc)}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <List>
                    {documents.map((doc) => (
                        <ListItem
                            key={doc.id}
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    color="error"
                                    disabled={isDisabled}
                                    onClick={() => onDelete(doc.id)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            }
                        >
                            <ListItemText
                                primary={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <a
                                            href={doc.fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {doc.fileName}
                                        </a>
                                        {isExpired(doc) && (
                                            <Chip
                                                label="Expired"
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                            />
                                        )}
                                    </Stack>
                                }
                                secondary={buildSecondary(doc)}
                            />
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
}
